import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import NoindexHead from "@/components/NoindexHead";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/25pknzhh_ee436b9cf26fbe3a949749993a8a0799c33dc4d497d0bf4b65f923b400bf1fc0.jpeg";
const API = `${window.location.origin}/api`;

export default function PortalSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);

  const verifyToken = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/verify-token/${token}`);
      setTokenValid(true);
      setCustomerName(response.data.customer_name || "");
    } catch (error) {
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token, verifyToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/set-password`, { token, password });
      setSuccess(true);
      toast.success("Password set successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set password");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
        <Helmet>
          <title>Password Set | Sustainable Handyman Services</title>
        </Helmet>
        
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">You're All Set!</h2>
            <p className="text-muted-foreground mb-6">
              Your password has been created. You can now log in to view your quotes and invoices.
            </p>
            <Button onClick={() => navigate("/portal")} className="w-full">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid/expired token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
        <Helmet>
          <title>Invalid Link | Sustainable Handyman Services</title>
        </Helmet>
        
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">Link Expired or Invalid</h2>
            <p className="text-muted-foreground mb-6">
              This setup link has expired or is invalid. Please contact us to request a new invite.
            </p>
            <div className="space-y-2">
              <Button variant="outline" onClick={() => navigate("/portal")} className="w-full">
                Go to Login
              </Button>
              <Button variant="ghost" onClick={() => navigate("/")} className="w-full">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password setup form
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
      <NoindexHead title="Set Up Your Password | Sustainable Handyman Services" />
      <Helmet>
        <title>Set Up Your Password | Sustainable Handyman Services</title>
      </Helmet>
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src={LOGO_URL} alt="Logo" className="h-16 w-16 rounded-full mx-auto mb-4 object-cover" />
          <CardTitle className="text-2xl font-heading">Welcome, {customerName}!</CardTitle>
          <CardDescription>Create a password to access your customer portal</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
                data-testid="setup-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Enter password again"
                required
                data-testid="setup-confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="setup-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Password
            </Button>
          </form>
          <p className="text-center text-xs text-muted-foreground mt-4">
            After setting your password, you'll be able to log in and view your quotes and invoices.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
